import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  CardBody,
  Collapse,
  Badge,
  Spinner,
  Row,
  Col,
  Button
} from 'reactstrap';
import {
  Video,
  Clock,
  Heart,
  Activity,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';
import axios from 'axios';
import MeasurementRow from './MeasurementRow';

const styles = {
  header: {
    background: '#ffffff',
    borderBottom: '1px solid #e3e6f0',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px'
  },
  cardBody: {
    backgroundColor: '#f8f9fa',
    borderLeft: '4px solid transparent'
  },
  bloodPressure: {
    background: '#fff8f8',
    borderLeft: '4px solid #e74a3b',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  bloodSugar: {
    background: '#f5f8ff',
    borderLeft: '4px solid #4e73df',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  },
  button: {
    primary: {
      background: '#4e73df',
      color: '#ffffff',
      border: 'none',
      boxShadow: '0 2px 4px rgba(78,115,223,0.25)'
    }
  }
};

const MeetingCard = ({ meeting, baseUrl, doctorJoinTime, doctorExitTime, patientJoinTime, patientExitTime }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meetingDetails, setMeetingDetails] = useState(null);

  const fetchMeetingDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(`${baseUrl}web/patientTeleconferenceMeetingDetails`, {
        meetingId: meeting.meetingId
      });
      setMeetingDetails(response.data);
    } catch (error) {
      console.error('Error fetching meeting details:', error);
      setError('Failed to load meeting details. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [meeting.meetingId, baseUrl]);

  useEffect(() => {
    if (isOpen && !meetingDetails && !error) {
      fetchMeetingDetails();
    }
  }, [isOpen, meetingDetails, fetchMeetingDetails, error]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'SET':
        return {
          color: 'success',
          label: 'Active',
          borderColor: '#10b981'
        };
      case 'CREATED':
        return {
          color: 'warning',
          label: 'Pending',
          borderColor: '#f59e0b'
        };
      case 'END':
        return {
          color: 'secondary',
          label: 'Ended',
          borderColor: '#6b7280'
        };
      default:
        return {
          color: 'info',
          label: status,
          borderColor: '#3b82f6'
        };
    }
  };

  const renderHealthMetrics = () => {
    if (!meetingDetails) return null;

    const hasValidBloodSugar = meetingDetails.bloodSugars?.length > 0 &&
      meetingDetails.bloodSugars[0]?.didBloodSugarMeasurementHappened === 'yes';

    if (!meetingDetails.bloodPressures?.length && !hasValidBloodSugar) {
      return null;
    }

    return (
      <div className="mt-3">
        <h6 className="mb-3 d-flex align-items-center">
          <Activity size={18} className="me-2" />
          Health Metrics
        </h6>
        <Row>
          {meetingDetails.bloodPressures?.length > 0 && (
            <Col md={6} className="mb-3">
              <Card className="h-100" style={styles.bloodPressure}>
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <Heart size={18} className="text-danger me-2" />
                    <h6 className="mb-0 fw-bold">Blood Pressure</h6>
                  </div>
                  {meetingDetails.bloodPressures.map((bp, index) => (
                    <div key={index} className="mb-2 bg-white border rounded p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <h4 className="mb-0 fw-bold text-danger">
                          {bp.systolic}/{bp.diastolic}
                          <small className="text-muted ms-2 fs-6 fw-normal">mmHg</small>
                        </h4>
                        <small className="text-muted">
                          {formatTime(bp.createdAt)}
                        </small>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            </Col>
          )}

          {hasValidBloodSugar && (
            <Col md={meetingDetails.bloodPressures?.length > 0 ? 6 : 12} className="mb-3">
              <Card className="h-100" style={styles.bloodSugar}>
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <Activity size={18} className="text-primary me-2" />
                    <h6 className="mb-0 fw-bold">Blood Sugar</h6>
                  </div>
                  {meetingDetails.bloodSugars.map((bs, index) => (
                    <div key={index} className="mb-2 bg-white border rounded p-3">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h4 className="mb-2 fw-bold text-primary">
                            {bs.bloodSugar}
                            <small className="text-muted ms-2 fs-6 fw-normal">mg/dL</small>
                          </h4>
                          {bs.lastMealTime && (
                            <div className="d-flex align-items-center">
                              <div className="badge bg-info-subtle text-info">
                                Last meal: {bs.lastMealTime} hours ago
                              </div>
                            </div>
                          )}
                        </div>
                        <small className="text-muted">
                          {formatTime(bs.createdAt)}
                        </small>
                      </div>
                    </div>
                  ))}
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  const statusConfig = getStatusConfig(meeting.meetingStatus);

  return (
    <Card className="mb-4 border">
      <CardBody className="p-0">
        <div
          className="p-3"
          style={{
            ...styles.header,
            borderLeft: `4px solid ${statusConfig.borderColor}`
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="d-flex align-items-center mb-2">
                <h5 className="mb-0 me-3">{meeting.patientName}</h5>
                <Badge color={statusConfig.color} pill>{statusConfig.label}</Badge>
              </div>
              <div className="d-flex align-items-center text-muted">
                <Activity size={14} className="me-2" />
                <small className="fw-medium">{meeting.studyName}</small>
                <span className="mx-2">•</span>
                <Clock size={14} className="me-2" />
                <small>{formatTime(meeting.meetingCreatedAt)}</small>
              </div>
              <div className="mt-2">
                <small className="me-3">
                  <strong>Doctor:</strong> {formatTime(doctorJoinTime)}
                  {doctorExitTime > 0 && ` - ${formatTime(doctorExitTime)}`}
                </small>
                <small>
                  <strong>Patient:</strong> {formatTime(patientJoinTime)}
                  {patientExitTime > 0 && ` - ${formatTime(patientExitTime)}`}
                </small>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <Video size={18} className="me-2 text-primary" />
              <span className="fs-5">{isOpen ? '▼' : '▲'}</span>
            </div>
          </div>
        </div>

        <Collapse isOpen={isOpen}>
          <div className="p-3">
            {/* Refresh Button */}
            <div className="d-flex justify-content-end mb-3">
              <Button
                color="primary"
                size="sm"
                className="px-3 py-2"
                style={styles.button.primary}
                onClick={fetchMeetingDetails}
                disabled={loading}
              >
                <RefreshCw size={14} className="me-2" />
                {loading ? 'Refreshing...' : 'Refresh Details'}
              </Button>
            </div>

            {loading && (
              <div className="text-center my-4">
                <Spinner color="primary" size="sm" />
              </div>
            )}

            {error && (
              <div className="alert alert-danger d-flex align-items-center">
                <AlertTriangle size={18} className="me-2" />
                <div>{error}</div>
              </div>
            )}

            {!loading && !error && meetingDetails && (
              <>

                {/* Measurements */}
                {meetingDetails.measurements?.map((measurement, index) => (
                  <MeasurementRow
                    key={index}
                    measurement={measurement}
                    index={index}
                  />
                ))}
                {/* Health Metrics at the top */}
                {renderHealthMetrics()}

              </>
            )}
          </div>
        </Collapse>
      </CardBody>
    </Card>
  );
};

export default MeetingCard;